import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { convex } from '../lib/convex';
import { api } from '../../convex/_generated/api';
import { type ColorScheme, getColorScheme } from '../utils/colorSchemes';
import logger from '../utils/logger';

interface UserPreferences {
  color_scheme: ColorScheme;
  notifications_email: boolean;
  notifications_calendar: boolean;
  notifications_members: boolean;
  preferred_currency: 'USD' | 'EUR';
}

interface UserPreferencesState {
  preferences: UserPreferences;
  isLoading: boolean;
  error: string | null;

  // Data setter (called from Convex sync)
  setPreferences: (preferences: UserPreferences) => void;

  // Actions
  loadPreferences: () => Promise<void>;
  updateColorScheme: (scheme: ColorScheme) => Promise<boolean>;
  updateNotificationSettings: (settings: Partial<Pick<UserPreferences, 'notifications_email' | 'notifications_calendar' | 'notifications_members'>>) => Promise<boolean>;
  updatePreferredCurrency: (currency: 'USD' | 'EUR') => Promise<boolean>;
  getSchemeData: () => ReturnType<typeof getColorScheme>;
  clearCache: () => void;
}

const defaultPreferences: UserPreferences = {
  color_scheme: 'default',
  notifications_email: true,
  notifications_calendar: true,
  notifications_members: true,
  preferred_currency: 'USD'
};

export const useUserPreferencesStore = create<UserPreferencesState>()(
  persist(
    (set, get) => ({
      preferences: defaultPreferences,
      isLoading: false,
      error: null,

      setPreferences: (preferences) => set({ preferences }),

      loadPreferences: async () => {
        logger.debug('Loading user preferences...');
        set({ isLoading: true, error: null });

        try {
          const data = await convex.query(api.users.queries.getPreferences);

          if (data) {
            const preferences: UserPreferences = {
              color_scheme: (data.colorScheme as ColorScheme) || 'default',
              notifications_email: data.notificationsEmail ?? true,
              notifications_calendar: data.notificationsCalendar ?? true,
              notifications_members: data.notificationsMembers ?? true,
              preferred_currency: (data.preferredCurrency === 'EUR' ? 'EUR' : 'USD') as 'USD' | 'EUR',
            };
            logger.debug('User preferences loaded');
            set({ preferences, isLoading: false });
          } else {
            logger.debug('No preferences found, using defaults');
            set({ preferences: defaultPreferences, isLoading: false });
          }
        } catch (error: any) {
          console.error('Error loading user preferences:', error);
          set({
            error: error.message || 'Error loading preferences',
            isLoading: false,
            preferences: defaultPreferences,
          });
        }
      },

      updateColorScheme: async (scheme: ColorScheme) => {
        logger.debug('Changing color scheme to:', scheme);
        set({ isLoading: true, error: null });

        try {
          await convex.mutation(api.users.mutations.updatePreferences, {
            colorScheme: scheme,
          });

          const currentPreferences = get().preferences;
          set({
            preferences: { ...currentPreferences, color_scheme: scheme },
            isLoading: false,
          });
          return true;
        } catch (error: any) {
          console.error('Error saving color scheme:', error);
          set({
            error: error.message || 'Error saving color scheme',
            isLoading: false,
          });
          return false;
        }
      },

      updateNotificationSettings: async (settings) => {
        logger.debug('Updating notification settings...');
        set({ isLoading: true, error: null });

        try {
          await convex.mutation(api.users.mutations.updatePreferences, {
            notificationsEmail: settings.notifications_email,
            notificationsCalendar: settings.notifications_calendar,
            notificationsMembers: settings.notifications_members,
          });

          const currentPreferences = get().preferences;
          set({
            preferences: { ...currentPreferences, ...settings },
            isLoading: false,
          });
          return true;
        } catch (error: any) {
          console.error('Error saving notification settings:', error);
          set({
            error: error.message || 'Error saving notification settings',
            isLoading: false,
          });
          return false;
        }
      },

      updatePreferredCurrency: async (currency) => {
        logger.debug('Updating preferred currency:', currency);
        set({ isLoading: true, error: null });

        try {
          await convex.mutation(api.users.mutations.updatePreferences, {
            preferredCurrency: currency,
          });

          const currentPreferences = get().preferences;
          set({
            preferences: { ...currentPreferences, preferred_currency: currency },
            isLoading: false,
          });
          return true;
        } catch (error: any) {
          console.error('Error saving preferred currency:', error);
          set({
            error: error.message || 'Error saving preferred currency',
            isLoading: false,
          });
          return false;
        }
      },

      getSchemeData: () => {
        const { preferences } = get();
        return getColorScheme(preferences.color_scheme);
      },

      clearCache: () => {
        logger.debug('Clearing user preferences cache');
        set({
          preferences: defaultPreferences,
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: 'user-preferences-storage',
      partialize: (state) => ({ preferences: state.preferences }),
    }
  )
);
